import { FormEvent } from "react"
import { Head, Link, useForm, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { FileInput } from "@/components/ui/file-input"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { KIND_LABELS } from "@/components/admin/audits"

import type { PageProps } from "@/types/inertia"

const DESCRIPTION =
  "Upload a built audit deck and publish it to a private, expiring URL."

type Props = {
  kinds: string[]
  default_expires_on: string
}

export default function AdminAuditsNew({ kinds, default_expires_on }: Props) {
  const errors = usePage<PageProps>().props.errors ?? {}

  // A File in useForm's data switches the request to multipart automatically.
  const form = useForm<{
    file: File | null
    client_name: string
    kind: string
    expires_at: string
  }>({
    file: null,
    client_name: "",
    kind: "",
    expires_at: default_expires_on,
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.post("/admin/audits")
  }

  return (
    <>
      <Head title="New audit">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="New audit" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell>
        <PageHeader
          title="New audit"
          description="The deck is validated before anything is stored, so a wrong file fails here rather than in front of a prospect."
          actions={
            <Button asChild variant="ghost">
              <Link href="/admin/audits">Cancel</Link>
            </Button>
          }
        />

        <form onSubmit={submit} className="mt-8 max-w-xl space-y-4">
          <div className="space-y-2">
            <label htmlFor="file">Deck file</label>
            <FileInput
              id="file"
              accept=".html,.htm,text/html"
              hint="The built .html deck, around 600 KB"
              value={form.data.file}
              onFileChange={(file) => form.setData("file", file)}
              invalid={!!errors.file}
              disabled={form.processing}
            />
            {errors.file ? (
              <p className="text-xs text-danger-display">{errors.file}</p>
            ) : (
              <p className="text-xs text-ink-muted">
                Not the PDF backup — the interactive HTML the deck builder produces.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="client_name">Client name</label>
            <Input
              id="client_name"
              required
              autoComplete="off"
              placeholder="Econo-Pak"
              aria-invalid={!!errors.client_name}
              value={form.data.client_name}
              onChange={(e) => form.setData("client_name", e.target.value)}
              disabled={form.processing}
            />
            {errors.client_name ? (
              <p className="text-xs text-danger-display">{errors.client_name}</p>
            ) : (
              <p className="text-xs text-ink-muted">
                Only ever shown to you, in this admin.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="kind">Kind</label>
            <Select
              id="kind"
              value={form.data.kind}
              onChange={(e) => form.setData("kind", e.target.value)}
              disabled={form.processing}
            >
              <option value="">No label</option>
              {kinds.map((kind) => (
                <option key={kind} value={kind}>
                  {KIND_LABELS[kind] ?? kind}
                </option>
              ))}
            </Select>
            {errors.kind && <p className="text-xs text-danger-display">{errors.kind}</p>}
          </div>

          <div className="space-y-2">
            <label htmlFor="expires_at">Expires</label>
            <Input
              id="expires_at"
              type="date"
              aria-invalid={!!errors.expires_at}
              value={form.data.expires_at}
              onChange={(e) => form.setData("expires_at", e.target.value)}
              disabled={form.processing}
            />
            {errors.expires_at ? (
              <p className="text-xs text-danger-display">{errors.expires_at}</p>
            ) : (
              <p className="text-xs text-ink-muted">
                90 days out by default. The link works until the end of this day,
                and you can change it or revoke it at any time.
              </p>
            )}
          </div>

          <Button type="submit" disabled={form.processing || !form.data.file}>
            {form.processing ? "Publishing…" : "Publish audit"}
          </Button>
        </form>
      </AdminShell>
    </>
  )
}
